import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  makeStyles,
  tokens,
  Field,
  Button
} from '@fluentui/react-components';
import { DismissRegular, ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { AppState } from '../../../redux/reducers';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';

// Components
import ProtocolSelectorComponent from '../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';
import ContractStagesSelectorComponent from '../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import ProtocolPatientSelectorComponent from '../../../commons/component/Inputs/ProtocolPatientSelector/ProtocolPatientSelector.Component';
import DateRangeSelectorComponent from '../../../commons/component/Inputs/DateRangeSelector/DateRangeSelector.Component';

// Store
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';

export interface ServiceFilterCriteria {
  protocolId?: number;
  stageId?: number;
  patientNumbers?: string[];
  startDate?: Date;
  endDate?: Date;
}

interface ServiceListMenuProps {
  onFilterChange: (filters: ServiceFilterCriteria) => void;
  currentFilters: ServiceFilterCriteria;
}

export const ServiceListMenu: React.FC<ServiceListMenuProps> = ({ onFilterChange, currentFilters }) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  const MedicalProtocolsState = useSelector((appState: AppState) => appState.MedicalProtocols);
  const ContractState = useSelector((appState: AppState) => appState.Contract);

  // Effect to load protocols if not already loaded
  useEffect(() => {
    if (!MedicalProtocolsState?.list || MedicalProtocolsState.list.length === 0) {
      dispatch(MedicalProtocolStore.actionCreators.GetAllMedicalProtocols());
    }
  }, [MedicalProtocolsState?.list, dispatch]);

  // Effect to load protocol-related data when filters are initialized from URL
  useEffect(() => {
    if (currentFilters.protocolId && MedicalProtocolsState?.list && MedicalProtocolsState.list.length > 0) {
      const selectedProtocol = MedicalProtocolsState.list.find((p: Protocol) => p.id === currentFilters.protocolId);
      
      // Load protocol patients if not already loaded
      if (!selectedProtocol?.patients || selectedProtocol.patients.length === 0) {
        dispatch(MedicalProtocolStore.actionCreators.GetProtocolPatients(currentFilters.protocolId, ''));
      }
      
      // Load contract association if not already loaded
      if (!selectedProtocol?.contractAssociation) {
        setTimeout(() => {
          dispatch(MedicalProtocolStore.actionCreators.GetAllProtocolContractAssociations(currentFilters.protocolId!));
        }, 100);
      }
    }
  }, [currentFilters.protocolId, MedicalProtocolsState?.list, dispatch]);

  // Memo to get selected protocols
  const selectedProtocols = useMemo(() => {
    if (currentFilters.protocolId && MedicalProtocolsState?.list) {
      const selectedProtocol = MedicalProtocolsState.list.find((p: Protocol) => p.id === currentFilters.protocolId);
      return selectedProtocol ? [selectedProtocol] : undefined;
    }
    return undefined;
  }, [currentFilters.protocolId, MedicalProtocolsState?.list]);

  // Get contractId from protocol association
  const contractId = useMemo(() => {
    if (currentFilters.protocolId && MedicalProtocolsState?.list) {
      const selectedProtocol = MedicalProtocolsState.list.find((p: Protocol) => p.id === currentFilters.protocolId);
      return selectedProtocol?.contractAssociation?.contractId;
    }
    return undefined;
  }, [currentFilters.protocolId, MedicalProtocolsState?.list]);

  // Effect to fetch contract if needed
  useEffect(() => {
    if (contractId) {
      const contractExists = ContractState?.list?.find((c: Contract) => c.id === contractId);
      if (!contractExists) {
        dispatch(ContractStore.actionCreators.GetContractById(contractId));
      }
    }
  }, [contractId, ContractState?.list, dispatch]);

  const handleProtocolChange = (selectedProtocols: Protocol[] | undefined) => {
    const newProtocolId = selectedProtocols && selectedProtocols.length > 0 ? selectedProtocols[0].id : undefined;
    
    // Load protocol data when user selects a protocol
    if (newProtocolId) {
      dispatch(MedicalProtocolStore.actionCreators.GetProtocolPatients(newProtocolId, ''));
      setTimeout(() => {
        dispatch(MedicalProtocolStore.actionCreators.GetAllProtocolContractAssociations(newProtocolId));
      }, 100);
    }
    
    onFilterChange({
      protocolId: newProtocolId,
      stageId: undefined,
      patientNumbers: []
    });
  };

  const handleStageChange = (selectedStages: ContractStage[]) => {
    const newStageId = selectedStages && selectedStages.length > 0 ? selectedStages[0].id : undefined;
    onFilterChange({
      ...currentFilters,
      stageId: newStageId
    });
  };

  const handlePatientChange = (selectedPatients: ProtocolPatient[]) => {
    const newPatientNumbers = selectedPatients?.map(p => p.numberInProtocol) || [];
    onFilterChange({
      ...currentFilters,
      patientNumbers: newPatientNumbers
    });
  };

  const handleDateRangeChange = (startDate: Date | undefined, endDate: Date | undefined) => {
    onFilterChange({
      ...currentFilters,
      startDate,
      endDate
    });
  };

  const handleClearFilters = () => {
    onFilterChange({
      protocolId: undefined,
      stageId: undefined,
      patientNumbers: [],
      startDate: undefined,
      endDate: undefined
    });
  };

  const hasActiveFilters = currentFilters.protocolId || currentFilters.stageId || (currentFilters.patientNumbers && currentFilters.patientNumbers.length > 0) || currentFilters.startDate || currentFilters.endDate;

  return (
    <div className={styles.menuContainer}>
      <div 
        className={isExpanded ? styles.menuHeaderExpanded : styles.menuHeaderCollapsed}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-label={isExpanded ? t('servicesListPage.filters.collapse') : t('servicesListPage.filters.expand')}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerContent}>
          <span className={styles.headerTitle}>{t('servicesListPage.filters.title')}</span>
          {hasActiveFilters && (
            <span className={styles.activeFiltersBadge}>
              {t('servicesListPage.filters.activeCount', { count: [
                currentFilters.protocolId,
                currentFilters.stageId,
                currentFilters.patientNumbers?.length,
                currentFilters.startDate,
                currentFilters.endDate
              ].filter(Boolean).length })}
            </span>
          )}
        </div>
        <Button
          appearance="subtle"
          icon={isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          aria-hidden="true"
        />
      </div>
      
      {isExpanded && (
        <div className={styles.filtersRow}>
        <Field 
          label={t('servicesListPage.filters.protocol')}
          className={styles.filterField}
        >
          <ProtocolSelectorComponent 
            selectionLimit={1} 
            selectedProtocols={selectedProtocols}
            onChangeSelection={handleProtocolChange}
          />
        </Field>

        <Field 
          label={t('servicesListPage.filters.visit')}
          className={styles.filterField}
        >
          {contractId && ContractState?.list?.find((c: Contract) => c.id === contractId) ? (
            <ContractStagesSelectorComponent
              ContractId={contractId}
              onSelectedContractStage={handleStageChange}
              ContractStagesSelected={currentFilters.stageId ? [currentFilters.stageId] : undefined}
              MultiselectEnable={false}
              showTitle={false}
            />
          ) : (
            <div className={styles.disabledPlaceholder}>
              {t('servicesListPage.filters.selectProtocolFirst')}
            </div>
          )}
        </Field>

        <Field 
          label={t('servicesListPage.filters.patient')}
          className={styles.filterField}
        >
          {currentFilters.protocolId ? (
            <ProtocolPatientSelectorComponent
              ProtocolId={currentFilters.protocolId}
              onSelectedProtocolPatient={handlePatientChange}
              ProtocolPatientsSelectedNumber={currentFilters.patientNumbers || []}
              MultiselectEnable={true}
              showTitle={false}
            />
          ) : (
            <div className={styles.disabledPlaceholder}>
              {t('servicesListPage.filters.selectProtocolFirst')}
            </div>
          )}
        </Field>

        <div className={styles.dateRangeField}>
          <DateRangeSelectorComponent
                startDate={currentFilters.startDate}
                endDate={currentFilters.endDate}
                onDateChange={handleDateRangeChange}
                startDateLabel={t('servicesListPage.filters.startDate')}
                endDateLabel={t('servicesListPage.filters.endDate')} 
                className={styles.dateRangeInline}          
            />
        </div>

        {hasActiveFilters && (
          <div className={styles.clearButton}>
            <Button
              appearance="secondary"
              icon={<DismissRegular />}
              onClick={handleClearFilters}
            >
              {t('servicesListPage.filters.clear')}
            </Button>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  menuContainer: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalM,
    boxShadow: tokens.shadow4
  },
  menuHeaderExpanded: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    userSelect: 'none'
  },
  menuHeaderCollapsed: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0',
    cursor: 'pointer',
    userSelect: 'none'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  activeFiltersBadge: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: tokens.borderRadiusCircular,
    padding: `2px ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold
  },
  filtersRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  filterField: {
    flex: 1,
    minWidth: '250px'
  },
  dateRangeField: {
    flex: 2,
    minWidth: '500px',
    display: 'flex',
    gap: tokens.spacingHorizontalM
  },
  dateRangeInline: {
    display: 'flex',
    flexDirection: 'row',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-start',
    '& > *': {
      flex: 1,
      minWidth: '200px'
    }
  },
  disabledPlaceholder: {
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForegroundDisabled,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase300
  },
  clearButton: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '27px'
  }
});

export default ServiceListMenu;
